import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";

const CmsContext = createContext({ get: (_k, fb) => fb, update: async () => {}, loaded: false });

const THEME_DEFAULTS = {
    "theme.primary": "#5a85c7",
    "theme.primary_soft": "#9cc1fa",
    "theme.background": "#0a1628",
};

function buildThemeCss(content) {
    const primary = content["theme.primary"] || THEME_DEFAULTS["theme.primary"];
    const primarySoft = content["theme.primary_soft"] || THEME_DEFAULTS["theme.primary_soft"];
    const bg = content["theme.background"] || THEME_DEFAULTS["theme.background"];
    // Override Tailwind compiled classes via !important + color-mix for opacity variants
    return `
:root { --cms-primary: ${primary}; --cms-primary-soft: ${primarySoft}; --cms-bg: ${bg}; }
html, body { background: ${bg} !important; }
.bg-ink-800 { background-color: ${bg} !important; }
.text-ice { color: ${primary} !important; }
.bg-ice { background-color: ${primary} !important; }
.bg-ice-soft { background-color: ${primarySoft} !important; }
.bg-ice\\/10 { background-color: color-mix(in srgb, ${primary} 10%, transparent) !important; }
.bg-ice\\/15 { background-color: color-mix(in srgb, ${primary} 15%, transparent) !important; }
.bg-ice\\/20 { background-color: color-mix(in srgb, ${primary} 20%, transparent) !important; }
.border-ice { border-color: ${primary} !important; }
.border-ice\\/15 { border-color: color-mix(in srgb, ${primary} 15%, transparent) !important; }
.border-ice\\/20 { border-color: color-mix(in srgb, ${primary} 20%, transparent) !important; }
.border-ice\\/30 { border-color: color-mix(in srgb, ${primary} 30%, transparent) !important; }
.border-ice\\/40 { border-color: color-mix(in srgb, ${primary} 40%, transparent) !important; }
.border-ice\\/50 { border-color: color-mix(in srgb, ${primary} 50%, transparent) !important; }
.text-ice\\/40 { color: color-mix(in srgb, ${primary} 40%, transparent) !important; }
.text-ice\\/60 { color: color-mix(in srgb, ${primary} 60%, transparent) !important; }
.text-ice\\/70 { color: color-mix(in srgb, ${primary} 70%, transparent) !important; }
.hover\\:text-ice:hover { color: ${primary} !important; }
.hover\\:bg-ice:hover { background-color: ${primary} !important; }
.hover\\:border-ice:hover { border-color: ${primary} !important; }
.hover\\:bg-ice-soft:hover { background-color: ${primarySoft} !important; }
`;
}

export function CmsProvider({ children }) {
    const [content, setContent] = useState({});
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/cms/content");
            setContent(data || {});
        } catch {
            // public endpoint; ignore errors silently
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Inject theme styles whenever content changes
    useEffect(() => {
        const styleId = "cms-theme-style";
        let el = document.getElementById(styleId);
        if (!el) {
            el = document.createElement("style");
            el.id = styleId;
            document.head.appendChild(el);
        }
        el.textContent = buildThemeCss(content);
    }, [content]);

    const get = useCallback((key, fallback = "") => {
        const v = content[key];
        return v === undefined || v === null || v === "" ? fallback : v;
    }, [content]);

    const update = useCallback(async (key, value) => {
        await api.put("/cms/content", { key, value });
        setContent((c) => ({ ...c, [key]: value }));
    }, []);

    const uploadImage = useCallback(async (key, file) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(
            `/cms/images/upload?key=${encodeURIComponent(key)}`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        setContent((c) => ({ ...c, [key]: data.value }));
        return data.value;
    }, []);

    const value = useMemo(
        () => ({ get, update, uploadImage, refresh, loaded, content }),
        [get, update, uploadImage, refresh, loaded, content]
    );

    return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() {
    return useContext(CmsContext);
}

export function useText(key, fallback) {
    const cms = useCms();
    return cms.get(key, fallback);
}
